import { DailyHireInterval, HourlyHireInterval } from "@/redux/slices/trip/types";

/**
 * Whether an interval carries real numbers.
 *
 * The hourly and daily pages both render `[basePrice, ...intervals]`, and `basePrice` is populated
 * straight from the API's `base_interval` — so a provider whose country has no seeded pricing gets
 * `{ minimumRange: undefined, rate: NaN }` rather than no interval at all, which reached the table
 * as a row reading "undefined+ hourly hire" priced at "£NaN" (DEV-990, a GB operator: the backend
 * seeds every default against a hardcoded 'AU', so nothing exists to read back for GB).
 *
 * Filtering here rather than at the page keeps any other caller from re-introducing the same row,
 * and empties the table so its empty state does the talking.
 */
export const isPricedInterval = (interval?: HourlyHireInterval | DailyHireInterval): boolean =>
  Number.isFinite(interval?.minimumRange) && Number.isFinite(interval?.rate);

/**
 * The base bracket's floor, for the editors.
 *
 * Zero is not a guess: it is what the seeded defaults carry (`hours => 0, base-element => true`) and
 * what the field being `disabled` in the editor encodes — the base bracket always starts at the
 * bottom. A country with no seeded pricing arrives with the whole base interval absent, which made
 * `basePrice.minimumRange + 1` append a row of NaN and left a disabled-and-required field with no
 * value to submit.
 *
 * Restored here rather than in the API transform on purpose: absence has to stay truthful on the
 * read path, or `isPricedInterval` stops excluding the base row and the tables go back to showing
 * "0+ hourly hire" at zero instead of their empty state. A table reports what exists; an editor
 * creates what does not.
 */
export const baseIntervalFloor = (interval?: HourlyHireInterval | DailyHireInterval): number =>
  Number.isFinite(interval?.minimumRange) ? (interval as HourlyHireInterval).minimumRange : 0;
